import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, TrendingDown, TrendingUp, X } from "lucide-react";
import { api, formatNum as fmt, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";

let formatNum = fmt;

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const Diff = ({ a, b, testid }) => {
  const d = b - a;
  return (
    <span className="font-mono-tab" data-testid={testid}>
      {formatNum(a)} → <strong>{formatNum(b)}</strong>
      {Math.abs(d) > 0.004 && (
        <span className={`ml-1 inline-flex items-center gap-0.5 text-[11px] font-bold ${d > 0 ? "text-rose-700" : "text-emerald-700"}`}>
          {d > 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}{d > 0 ? "+" : ""}{formatNum(d)}
        </span>
      )}
    </span>
  );
};

export const PositionImpactModal = ({ yil, ay, changes, title, onConfirm, onClose }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { canSeeMoney } = useAuth();
  formatNum = canSeeMoney ? fmt : () => "•••••";
  useEffect(() => {
    api.post("/position-templates/preview-impact", { yil, ay, changes })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(extractErrorMessage(e, "Önizleme alınamadı")); onClose(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4" onClick={(e) => e.stopPropagation()} data-testid="position-impact-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <div className="font-bold text-sm">Değişiklik Özeti · {title}</div>
            <div className="text-[11px] text-slate-500">{AYLAR[ay - 1]} {yil} bordrosuna göre aylık yemek ve yol toplamları</div>
          </div>
          <button onClick={onClose} data-testid="position-impact-close" className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={14} /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-3">
          {!data ? <div className="text-center py-6"><Loader2 size={16} className="inline animate-spin" /></div> : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <div className="text-[10px] font-bold uppercase text-indigo-700">Yemek toplamı</div>
                  <div className="text-sm"><Diff a={data.totals.yemek_once} b={data.totals.yemek_sonra} testid="impact-yemek-total" /></div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-[10px] font-bold uppercase text-slate-600">Yol toplamı</div>
                  <div className="text-sm"><Diff a={data.totals.yol_once} b={data.totals.yol_sonra} testid="impact-yol-total" /></div>
                </div>
              </div>
              {data.positions.length > 1 && (
                <table className="w-full text-xs" data-testid="impact-positions">
                  <thead><tr className="text-slate-500"><th className="text-left">Pozisyon</th><th>Kişi</th><th className="text-right">Yemek</th><th className="text-right">Yol</th></tr></thead>
                  <tbody>{data.positions.map((p) => (
                    <tr key={p.pozisyon} className="border-t border-slate-100"><td className="py-1 font-semibold">{p.pozisyon}</td><td className="text-center">{p.kisi}</td>
                      <td className="text-right"><Diff a={p.yemek_once} b={p.yemek_sonra} /></td><td className="text-right"><Diff a={p.yol_once} b={p.yol_sonra} /></td></tr>
                  ))}</tbody>
                </table>
              )}
              <div className="text-xs font-bold text-slate-700">Etkilenen personel ({data.people.length} / {data.kisi})</div>
              {data.people.length === 0 ? <div className="text-xs text-slate-500" data-testid="impact-no-change">Bu ay için tutar değişmiyor.</div> : (
                <table className="w-full text-xs" data-testid="impact-people">
                  <thead><tr className="text-slate-500"><th className="text-left">Ad Soyad</th><th>Yemek günü</th><th className="text-right">Yemek</th><th className="text-right">Yol</th></tr></thead>
                  <tbody>{data.people.map((r) => (
                    <tr key={r.ad_soyad + r.pozisyon} className="border-t border-slate-100">
                      <td className="py-1 font-semibold">{r.ad_soyad}<div className="text-[10px] font-normal text-slate-400">{r.pozisyon}</div></td>
                      <td className="text-center font-mono-tab">{r.gun_once} → {r.gun_sonra}</td>
                      <td className="text-right"><Diff a={r.yemek_once} b={r.yemek_sonra} /></td>
                      <td className="text-right"><Diff a={r.yol_once} b={r.yol_sonra} /></td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold">Vazgeç</button>
          <button onClick={confirm} disabled={!data || busy} data-testid="position-impact-confirm"
            className="px-3 py-1.5 bg-teal-700 hover:bg-teal-800 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
            {busy ? <Loader2 size={14} className="inline animate-spin" /> : "Onayla ve Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
};
